import { useEffect, useState } from "react";

import type { ConsentCopySummary } from "../../../shared/ui/consent/ConsentCopiesList";
import type { Patient } from "../domain/Patient";
import type { InvitationLanguage, PatientInvitation } from "../domain/PatientInvitation";
import { PatientsAPI, type PatientConsentStatus } from "../infrastructure/api/patients.api";

/**
 * Everything the compliance tab shows and does for one patient: their portal
 * invitations, their consent and its signed copies, and the rendered consent
 * document.
 */
export function usePatientCompliance(patient: Patient) {
    const [invitations, setInvitations] = useState<PatientInvitation[]>([]);
    const [sendingInvitation, setSendingInvitation] = useState(false);
    const [invitationFeedback, setInvitationFeedback] = useState<"sent" | "error" | null>(null);
    const [invitationLanguage, setInvitationLanguage] = useState<InvitationLanguage>("es");

    const [consentStatus, setConsentStatus] = useState<PatientConsentStatus | null>(null);
    const [consentActionLoading, setConsentActionLoading] = useState(false);
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
    const [consentCopies, setConsentCopies] = useState<ConsentCopySummary[]>([]);
    const [downloadingCopyId, setDownloadingCopyId] = useState<string | null>(null);
    const [consentDocumentUrl, setConsentDocumentUrl] = useState<string | null>(null);
    const [consentDocumentFailed, setConsentDocumentFailed] = useState(false);

    // The document is fetched rather than linked: it is rendered per patient and
    // sits behind the therapist's token, which a plain <a href> would not carry.
    useEffect(() => {
        if (!patient?.id) return;
        let objectUrl: string | null = null;
        PatientsAPI.getConsentDocumentPdf(patient.id)
            .then(({ data }) => {
                objectUrl = URL.createObjectURL(data);
                setConsentDocumentUrl(objectUrl);
            })
            .catch(() => setConsentDocumentFailed(true));
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [patient?.id]);

    useEffect(() => {
        if (!patient?.id) return;
        PatientsAPI.listInvitations(patient.id)
            .then((r) => setInvitations(r.data))
            .catch(() => {});
        PatientsAPI.getConsent(patient.id)
            .then((r) => setConsentStatus(r.data))
            .catch(() => setConsentStatus({ is_active: false }));
        PatientsAPI.getConsentCopies(patient.id)
            .then((r) => setConsentCopies(r.data))
            .catch(() => setConsentCopies([]));
    }, [patient?.id]);

    const latestInvitation = invitations.length > 0 ? invitations[0] : null;

    const sendInvitation = async () => {
        if (!patient.contact_email) return;
        setSendingInvitation(true);
        setInvitationFeedback(null);
        try {
            const r = await PatientsAPI.sendInvitation(patient.id as string, {
                email: patient.contact_email,
                language: invitationLanguage,
            });
            setInvitations([r.data, ...invitations]);
            setInvitationFeedback("sent");
        } catch {
            setInvitationFeedback("error");
        } finally {
            setSendingInvitation(false);
        }
    };

    const recordConsent = async () => {
        setConsentActionLoading(true);
        try {
            const { data } = await PatientsAPI.acceptConsentOnBehalf(patient.id!);
            setConsentStatus(data);
        } catch {
            // silent
        } finally {
            setConsentActionLoading(false);
        }
    };

    const downloadConsentCopy = async (copyId: string) => {
        if (!patient.id) return;
        setDownloadingCopyId(copyId);
        try {
            const { data } = await PatientsAPI.getConsentCopyUrl(patient.id, copyId);
            window.open(data.download_url, "_blank", "noopener,noreferrer");
        } catch {
            // Silent: the list only offers copies the server just listed.
        } finally {
            setDownloadingCopyId(null);
        }
    };

    const revokeConsent = async () => {
        setConsentActionLoading(true);
        try {
            const { data } = await PatientsAPI.revokeConsentOnBehalf(patient.id!);
            setConsentStatus(data);
            setShowRevokeConfirm(false);
        } catch {
            // silent
        } finally {
            setConsentActionLoading(false);
        }
    };

    return {
        invitations,
        latestInvitation,
        sendingInvitation,
        invitationFeedback,
        invitationLanguage,
        setInvitationLanguage,
        sendInvitation,
        consentStatus,
        consentActionLoading,
        showRevokeConfirm,
        setShowRevokeConfirm,
        recordConsent,
        revokeConsent,
        consentCopies,
        downloadingCopyId,
        downloadConsentCopy,
        consentDocumentUrl,
        consentDocumentFailed,
    };
}
